import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { RouteLocationNormalizedLoaded } from 'vue-router';

export interface TagView {
  path: string;
  name?: string | symbol;
  title?: string;
  meta?: any;
  fullPath: string;
  query?: Record<string, any>;
  params?: Record<string, any>;
}

const useTagsViewStore = defineStore(
  'tagsView',
  () => {
    // 访问过的视图（标签列表）
    const visitedViews = ref<TagView[]>([]);
    // 缓存的视图（用于 keep-alive）
    const cachedViews = ref<string[]>([]);

    /**
     * 添加访问的视图
     */
    const addVisitedView = (route: RouteLocationNormalizedLoaded) => {
      // 如果路由没有 title，不添加
      if (!route.meta?.title) {
        return;
      }

      // 检查是否已存在
      const exists = visitedViews.value.some(v => v.path === route.path);

      if (!exists) {
        const tag: TagView = {
          path: route.path,
          name: route.name,
          title: route.meta.title as string,
          meta: route.meta,
          fullPath: route.fullPath,
          query: route.query,
          params: route.params
        };

        visitedViews.value.push(tag);
      } else {
        // 如果已存在，更新查询参数和 fullPath
        const index = visitedViews.value.findIndex(v => v.path === route.path);

        if (index > -1) {
          visitedViews.value[index] = {
            ...visitedViews.value[index],
            fullPath: route.fullPath,
            query: route.query,
            params: route.params
          };
        }
      }
    };

    /**
     * 添加缓存的视图
     */
    const addCachedView = (route: RouteLocationNormalizedLoaded) => {
      if (!route.name || route.meta?.noCache) {
        return;
      }

      const routeName = route.name as string;

      if (!cachedViews.value.includes(routeName)) {
        cachedViews.value.push(routeName);
      }
    };

    /**
     * 添加视图（同时添加到 visited 和 cached）
     */
    const addView = (route: RouteLocationNormalizedLoaded) => {
      addVisitedView(route);
      addCachedView(route);
    };

    /**
     * 删除访问的视图
     */
    const delVisitedView = (tag: TagView): Promise<TagView[]> => {
      return new Promise(resolve => {
        // 不能删除 Dashboard（path 为 /dashboard）
        if (tag.path === '/dashboard') {
          // console.warn('不能关闭 Dashboard 标签');
          resolve([...visitedViews.value]);

          return;
        }

        const index = visitedViews.value.findIndex(v => v.path === tag.path);

        if (index > -1) {
          visitedViews.value.splice(index, 1);
        }

        resolve([...visitedViews.value]);
      });
    };

    /**
     * 删除缓存的视图
     */
    const delCachedView = (tag: TagView): Promise<string[]> => {
      return new Promise(resolve => {
        if (!tag.name) {
          resolve([...cachedViews.value]);

          return;
        }

        const routeName = tag.name as string;
        const index = cachedViews.value.indexOf(routeName);

        if (index > -1) {
          cachedViews.value.splice(index, 1);
        }

        resolve([...cachedViews.value]);
      });
    };

    /**
     * 删除视图（同时从 visited 和 cached 中删除）
     */
    const delView = (tag: TagView): Promise<{ visitedViews: TagView[], cachedViews: string[], }> => {
      return new Promise(resolve => {
        void delVisitedView(tag);
        void delCachedView(tag);

        resolve({
          visitedViews: [...visitedViews.value],
          cachedViews: [...cachedViews.value]
        });
      });
    };

    /**
     * 删除其他视图
     */
    const delOthersViews = (tag: TagView): Promise<{ visitedViews: TagView[], cachedViews: string[], }> => {
      return new Promise(resolve => {
        // 保留固定的标签（affix）和当前标签
        visitedViews.value = visitedViews.value.filter(v => {
          return v.meta?.affix || v.path === tag.path;
        });

        // 保留当前缓存
        if (tag.name) {
          const routeName = tag.name as string;

          cachedViews.value = cachedViews.value.filter(name => name === routeName);
        } else {
          cachedViews.value = [];
        }

        resolve({
          visitedViews: [...visitedViews.value],
          cachedViews: [...cachedViews.value]
        });
      });
    };

    /**
     * 删除所有视图
     */
    const delAllViews = (): Promise<{ visitedViews: TagView[], cachedViews: string[], }> => {
      return new Promise(resolve => {
        // 只保留固定的标签（affix）
        visitedViews.value = visitedViews.value.filter(v => v.meta?.affix);
        cachedViews.value = [];

        resolve({
          visitedViews: [...visitedViews.value],
          cachedViews: [...cachedViews.value]
        });
      });
    };

    /**
     * 删除左侧所有视图
     */
    const delLeftViews = (tag: TagView): Promise<{ visitedViews: TagView[], cachedViews: string[], }> => {
      return new Promise(resolve => {
        const currentIndex = visitedViews.value.findIndex(v => v.path === tag.path);

        if (currentIndex === -1) {
          resolve({
            visitedViews: [...visitedViews.value],
            cachedViews: [...cachedViews.value]
          });

          return;
        }

        visitedViews.value = visitedViews.value.filter((v, index) => {
          return index >= currentIndex || v.meta?.affix;
        });

        resolve({
          visitedViews: [...visitedViews.value],
          cachedViews: [...cachedViews.value]
        });
      });
    };

    /**
     * 删除右侧所有视图
     */
    const delRightViews = (tag: TagView): Promise<{ visitedViews: TagView[], cachedViews: string[], }> => {
      return new Promise(resolve => {
        const currentIndex = visitedViews.value.findIndex(v => v.path === tag.path);

        if (currentIndex === -1) {
          resolve({
            visitedViews: [...visitedViews.value],
            cachedViews: [...cachedViews.value]
          });

          return;
        }

        visitedViews.value = visitedViews.value.filter((v, index) => {
          return index <= currentIndex || v.meta?.affix;
        });

        resolve({
          visitedViews: [...visitedViews.value],
          cachedViews: [...cachedViews.value]
        });
      });
    };

    /**
     * 更新访问的视图
     */
    const updateVisitedView = (route: RouteLocationNormalizedLoaded) => {
      const index = visitedViews.value.findIndex(v => v.path === route.path);

      if (index > -1) {
        visitedViews.value[index] = {
          ...visitedViews.value[index],
          fullPath: route.fullPath,
          title: route.meta.title as string,
          meta: route.meta
        };
      }
    };

    /**
     * 清理无效的视图（用于路由加载后验证）
     */
    const cleanInvalidViews = (validPaths: string[]) => {
      visitedViews.value = visitedViews.value.filter(v =>
        validPaths.includes(v.path) || v.meta?.affix
      );
    };

    return {
      visitedViews,
      cachedViews,
      addView,
      addVisitedView,
      addCachedView,
      delView,
      delVisitedView,
      delCachedView,
      delOthersViews,
      delAllViews,
      delLeftViews,
      delRightViews,
      updateVisitedView,
      cleanInvalidViews
      // $resetAll 方法由 resetPlugin 自动添加，无需手动定义
    };
  },
  {
    persist: {
      key: 'tags-view-store',
      storage: sessionStorage,
      paths: ['visitedViews'] // 只持久化访问过的视图
    }
  }
);

export default useTagsViewStore;
